import { useEffect, useState } from 'react';
import { EXPERIENCE_PROGRAMS_DAYS_SCHEMA } from '../Utilities/ExperienceValidationSchemas';
import DayPills from 'components/DayPills';
import { Container, Warning } from 'ui';
import { DayForm } from '../Partials/DayForm';
import { FieldArray, useField } from 'formik';
import { initialLocale } from '../../../../utils';
import { useTranslation } from 'react-i18next';
import KoobTabs from 'components/Koob/KoobTabs';
import { Buttons, Stack } from '@koob/margaret';
import { KoobButton, KoobCard } from '@koob/ui';
import { EditorField } from 'components/Fields';
import { ExperienceTypeEnum } from '@koob/enums';
import { Modal } from 'components';

export const ExperienceItinerary = ({ currentLocale, isReadOnly = false }) => {
  const { t } = useTranslation('experiences');
  const [{ value: experienceType }, ,] = useField('type');
  const [, , { setValue: setDurationDays }] = useField('durationDays');
  const [daysData, ,] = useField('programs');
  const [selectedDayIndex, setSelectedDayIndex] = useState(
    daysData?.value?.length ? 0 : -1,
  );

  const [{ value }, , { setValue }] = useField('summary');

  useEffect(() => {
    if (value?.length > 150) {
      const summaryTroncate = value.slice(0, 150);
      setValue(summaryTroncate);
    }
  }, [value]);

  const createDayIndex = () => {
    setSelectedDayIndex(daysData?.value?.length ?? 0);
  };

  const removeDayIndex = (index) => {
    if (selectedDayIndex === index) {
      setSelectedDayIndex(daysData?.value?.length - 2);
    }
  };

  const tabs = [
    {
      id: 'program',
      label: t('steps.itinerary.description'),
    },
    {
      id: 'itinerary',
      label: t('form.summary'),
    },
  ];

  useEffect(() => {
    if (experienceType === ExperienceTypeEnum.PROGRAM) {
      setDurationDays(daysData?.value?.length);
    }
  }, [daysData?.value?.length]);

  const [currentTab, setCurrentTab] = useState(tabs[0]?.id);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [setIndexToRemove, setSetIndexToRemove] = useState({});

  const handleRemove = () => {
    setIndexToRemove.helpers.remove(setIndexToRemove.index);
    removeDayIndex(setIndexToRemove.index);
    setShowConfirmModal(false);
  };

  return (
    <Container size="full">
      <KoobTabs
        tabs={tabs}
        currentTab={currentTab}
        setCurrentTab={setCurrentTab}
      />

      <div className="my-5">
        <Stack gutterSize={1.5}>
          {currentTab === 'program' && (
            <div className="w-full relative">
              <FieldArray
                name={'programs'}
                render={(helpers) => {
                  return (
                    <>
                      <DayPills
                        selectedDayIndex={selectedDayIndex}
                        onSelectDayIndex={setSelectedDayIndex}
                        days={daysData.value}
                        createDayIndex={() => {
                          helpers.push({});
                          createDayIndex();
                        }}
                        deleteDayIndex={(index) => {
                          setShowConfirmModal(true);
                          setSetIndexToRemove({ index, helpers });
                        }}
                        translation="experiences"
                        disabled={
                          currentLocale.value !== initialLocale.value ||
                          isReadOnly
                        }
                      />
                      <KoobCard style={{ marginBottom: 32 }}>
                        <DayForm
                          index={selectedDayIndex}
                          currentLocale={currentLocale}
                          isReadOnly={isReadOnly}
                        />
                      </KoobCard>
                    </>
                  );
                }}
              />
            </div>
          )}

          {currentTab === 'itinerary' && (
            <EditorField
              name={'summary'}
              label={t('form.summary')}
              disabled={isReadOnly}
            />
          )}
        </Stack>
      </div>

      <Modal
        onRequestClose={() => setShowConfirmModal(false)}
        isOpen={showConfirmModal}
        title={t('misc:warning')}
        footer={
          <Buttons alignX="center">
            <KoobButton
              type="button"
              onClick={() => setShowConfirmModal(false)}
              variant="secondary"
            >
              {t('misc:no')}
            </KoobButton>
            <KoobButton variant="primary" type="submit" onClick={handleRemove}>
              {t('misc:yes')}
            </KoobButton>
          </Buttons>
        }
        contentHasOwnScrollbars
      >
        <Warning>{t('steps.itinerary.dayWarning')}</Warning>
      </Modal>
    </Container>
  );
};

export const getSchema = () => EXPERIENCE_PROGRAMS_DAYS_SCHEMA;
